"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SentencePanel = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Copyright 2025 Scott Bender (scott@scottbender.net)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const react_1 = require("react");
const ts_pgns_1 = require("@canboat/ts-pgns");
const rxjs_1 = require("rxjs");
const observable_hooks_1 = require("observable-hooks");
const EditorTab_1 = require("./EditorTab");
const reactstrap_1 = require("reactstrap");
// Import the separated tab components
const HumanReadableTab_1 = require("./HumanReadableTab");
const JsonDataTab_1 = require("./JsonDataTab");
const InputDataTab_1 = require("./InputDataTab");
const DeviceInfoTab_1 = require("./DeviceInfoTab");
const PgnDefinitionTab_1 = require("./PgnDefinitionTab");
const ByteMappingTab_1 = require("./ByteMappingTab");
const DATA_TAB_ID = 'data';
const PGNDEF_TAB_ID = 'pgndef';
const DEVICE_TAB_ID = 'device';
const INPUT_TAB_ID = 'input';
const MAPPING_TAB_ID = 'mapping';
const READABLE_TAB_ID = 'readable';
const SentencePanel = (props) => {
    const [activeTab, setActiveTab] = (0, react_1.useState)(READABLE_TAB_ID);
    const pgnData = (0, observable_hooks_1.useObservableState)(props.selectedPgn);
    const pgnWithHistory = (0, observable_hooks_1.useObservableState)(props.selectedPgnWithHistory || new rxjs_1.Subject());
    const info = (0, observable_hooks_1.useObservableState)(props.info, {});
    // Track when definitions change
    const notifyDefinitionsChanged = (0, react_1.useCallback)(() => {
        if (props.onDefinitionsChanged) {
            props.onDefinitionsChanged(EditorTab_1.changedDefinitionsTracker.getChangedDefinitions());
        }
    }, [props.onDefinitionsChanged]);
    const copyPgnData = async () => {
        if (!pgnData)
            return;
        const dataToSave = JSON.stringify(pgnData, (key, value) => key === 'input' || key === 'rawData' || key === 'byteMapping' || key === 'definition' ? undefined : value, 2);
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(dataToSave);
            }
            else {
                // Fallback for browsers that don't support clipboard API
                const textarea = document.createElement('textarea');
                textarea.value = dataToSave;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
        }
        catch (err) {
            console.error('Failed to copy PGN data:', err);
        }
    };
    const copyInputData = async () => {
        if (!pgnData?.input)
            return;
        const inputDataToSave = pgnData.input.join('\n');
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(inputDataToSave);
            }
            else {
                // Fallback for browsers that don't support clipboard API
                const textarea = document.createElement('textarea');
                textarea.value = inputDataToSave;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
        }
        catch (err) {
            console.error('Failed to copy input data:', err);
        }
    };
    const handleDefinitionSave = async (updatedDefinition) => {
        try {
            // Track this definition as changed us`ing the PGN Id
            if (!pgnData) {
                return;
            }
            (0, EditorTab_1.saveDefinition)(updatedDefinition, pgnData);
            notifyDefinitionsChanged();
            if (props.onDefinitionSave) {
                props.onDefinitionSave(updatedDefinition);
            }
        }
        catch (err) {
            console.error('Failed to save definition:', err);
        }
    };
    const handleDefinitionExport = async (definition) => {
        try {
            const definitionJson = JSON.stringify(definition, null, 2);
            // Try to save as a file if possible, otherwise copy to clipboard
            if ('showSaveFilePicker' in window) {
                try {
                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: `pgn_${definition.PGN}_${definition.Id}.json`,
                        types: [
                            {
                                description: 'JSON files',
                                accept: { 'application/json': ['.json'] },
                            },
                        ],
                    });
                    const writable = await fileHandle.createWritable();
                    await writable.write(definitionJson);
                    await writable.close();
                    console.log('Definition exported to file:', definition.Id);
                    return;
                }
                catch (exportErr) {
                    // User cancelled file picker or other error, fall back to clipboard
                    if (exportErr.name !== 'AbortError') {
                        console.warn('File picker failed, falling back to clipboard:', exportErr);
                    }
                }
            }
            // Fallback to clipboard
            await navigator.clipboard.writeText(definitionJson);
            console.log('Definition exported to clipboard:', definition.Id);
            // You could show a toast notification here if you have a toast system
            alert('PGN definition exported to clipboard!');
        }
        catch (err) {
            console.error('Failed to export definition:', err);
            alert('Failed to export definition. Please try again.');
        }
    };
    const handleInputDataChange = (0, react_1.useCallback)((newInput) => {
        if (pgnData && props.definition) {
            // Update the PGN data with new input
            //pgnData.input = newInput
            // Trigger any necessary updates
            //props.selectedPgn.next(pgnData)
            //(props.definition as any).sampleData = newInput
            pgnData.input = newInput;
            if (props.onDefinitionSave) {
                props.onDefinitionSave(props.definition);
            }
        }
    }, [pgnData, props.selectedPgn]);
    const handleLookupSave = (0, react_1.useCallback)((enumName, lookupType, lookupValues) => {
        if (!enumName) {
            console.warn('No enumeration name provided for lookup save');
            return;
        }
        try {
            if (lookupType === 'lookup') {
                // Create regular enumeration from lookup values
                const enumeration = {
                    Name: enumName,
                    MaxValue: Math.max(...lookupValues.map((lv) => parseInt(lv.key, 10))),
                    EnumValues: lookupValues.map((lv) => ({
                        Name: lv.value,
                        Value: parseInt(lv.key, 10),
                    })),
                };
                EditorTab_1.changedDefinitionsTracker.addLookup(enumeration);
                (0, ts_pgns_1.updateLookup)(enumeration);
            }
            else {
                // Create bit enumeration from lookup values
                const bitEnumeration = {
                    Name: enumName,
                    MaxValue: Math.max(...lookupValues.map((lv) => parseInt(lv.key, 10))),
                    EnumBitValues: lookupValues.map((lv) => ({
                        Name: lv.value,
                        Bit: parseInt(lv.key, 10),
                    })),
                };
                EditorTab_1.changedDefinitionsTracker.addLookup(bitEnumeration);
                (0, ts_pgns_1.updateBitLookup)(bitEnumeration);
            }
            console.log(`Successfully updated ${lookupType} enumeration: ${enumName}`);
        }
        catch (error) {
            console.error(`Failed to save ${lookupType} enumeration:`, error);
        }
    }, []);
    if (pgnData === undefined || pgnData === null) {
        return (0, jsx_runtime_1.jsx)("div", { children: "Select a PGN to view its data" });
    }
    //let definition: Definition = pgnData.getDefinition()
    let definition = props.definition || pgnData.getDefinition();
    //console.debug('pgnData', pgnData)
    return ((0, jsx_runtime_1.jsxs)("div", { style: {
            width: '100%',
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
        }, children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.Nav, { tabs: true, children: [(0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeTab === READABLE_TAB_ID ? 'active ' : '', onClick: () => setActiveTab(READABLE_TAB_ID), children: "Data" }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeTab === DATA_TAB_ID ? 'active ' : '', onClick: () => setActiveTab(DATA_TAB_ID), children: "JSON" }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeTab === INPUT_TAB_ID ? 'active ' : '', onClick: () => setActiveTab(INPUT_TAB_ID), children: "Input" }) }), info[pgnData.src]?.info && ((0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeTab === DEVICE_TAB_ID ? 'active ' : '', onClick: () => setActiveTab(DEVICE_TAB_ID), children: "Device Info" }) })), !props.inEditingTab && ((0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeTab === PGNDEF_TAB_ID ? 'active ' : '', onClick: () => setActiveTab(PGNDEF_TAB_ID), children: "Definition" }) })), (0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeTab === MAPPING_TAB_ID ? 'active ' : '', onClick: () => setActiveTab(MAPPING_TAB_ID), children: "Byte Mapping" }) })] }), (0, jsx_runtime_1.jsxs)(reactstrap_1.TabContent, { activeTab: activeTab, style: { flex: 1, overflow: 'auto' }, children: [(0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: READABLE_TAB_ID, children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { children: (0, jsx_runtime_1.jsx)(HumanReadableTab_1.HumanReadableTab, { pgnData: pgnData, definition: definition, pgnHistory: pgnWithHistory?.history || [] }) }) }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: DATA_TAB_ID, children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { style: { padding: 0 }, children: (0, jsx_runtime_1.jsx)(JsonDataTab_1.JsonDataTab, { pgnData: pgnData, pgnHistory: pgnWithHistory?.history || [], onCopyData: copyPgnData }) }) }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: INPUT_TAB_ID, children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { style: { padding: 0 }, children: (0, jsx_runtime_1.jsx)(InputDataTab_1.InputDataTab, { pgnData: pgnData, onCopyInput: copyInputData, isEditing: props.inEditingTab, onInputChange: handleInputDataChange }) }) }) }), definition !== undefined && !props.inEditingTab && ((0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: PGNDEF_TAB_ID, children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { style: { padding: 0 }, children: (0, jsx_runtime_1.jsx)(PgnDefinitionTab_1.PgnDefinitionTab, { definition: definition, pgnData: pgnData, onSave: handleDefinitionSave, hasBeenChanged: EditorTab_1.changedDefinitionsTracker.hasDefinition(definition.Id), onExport: handleDefinitionExport, onEditPgn: props.inEditingTab === false ? props.onEditPgn : undefined, changedLookups: EditorTab_1.changedDefinitionsTracker.getChangedLookups() }, pgnData.pgn) }) }) })), info[pgnData.src]?.info && ((0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: DEVICE_TAB_ID, children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { style: { padding: 0 }, children: (0, jsx_runtime_1.jsx)(DeviceInfoTab_1.DeviceInfoTab, { pgnData: pgnData, info: info }) }) }) })), (0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: MAPPING_TAB_ID, children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Card, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.CardHeader, { children: (0, jsx_runtime_1.jsxs)("small", { children: [(0, jsx_runtime_1.jsx)("strong", { children: "PGN:" }), " ", pgnData.pgn, " |", (0, jsx_runtime_1.jsx)("strong", { children: " Source:" }), " ", pgnData.src, " |", (0, jsx_runtime_1.jsx)("strong", { children: " Destination:" }), " ", pgnData.dst, (0, jsx_runtime_1.jsx)("br", {}), (0, jsx_runtime_1.jsx)("strong", { children: "Description:" }), " ", pgnData.description || 'N/A'] }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { children: (0, jsx_runtime_1.jsx)(ByteMappingTab_1.ByteMappingTab, { pgnData: pgnData, definition: definition }) })] }) })] })] }));
};
exports.SentencePanel = SentencePanel;
